import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchChannelConfig, fetchChannelNames } from "../../../api/channel";
import { getComparator, stableSort } from "../../channelPage/utils/helper";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { setCustomDataDuration } from "../../../model/slice/dataDurationSlice";
import { durationInitialState } from "../../../constants/duration";
import { DEFAULT_CUSTOM_CHART_KEY } from "../../../constants/trendGraph/charts";
import { CustomGraph } from "../customGraph";
import { useTranslation } from "react-i18next";
import { DisplayGraphHeader } from "./displayGraphHeader";
import { DisplayChart } from "./displayChart";
import { Box, useTheme } from "@mui/material";
import { useStyles } from "../trendPageCommonStyles";
import localizationKeys from "../../../i18n/localizationKeys";
import dayjs from "dayjs";
import { LATEST_UPDATE_FORMAT } from "../../../constants/util";
import PropTypes from "prop-types";

export const DisplayCustomGraphPage = (props) => {
  const {
    vesselId,
    customCharts,
    isCreateCustomCharts,
    setIsCreateCustomCharts,
    selectedChart,
    selectedGraph,
    isSepVessel,
    selectedEngine,
    setSelectedEngine,
    currentVessel,
    trendCharts,
    isVesselOverview,
    setChartRef,
    overviewTrendIdx,
    setIsCustomChartsCreated,
    setIsChartDeleted,
    customChartTitle,
    setCustomChartTitle,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useStyles(theme);
  const dispatch = useDispatch();
  const [channels, setChannels] = useState([]);
  const [selectedChartData, setSelectedChartData] = useState({});

  const [graphData, setGraphData] = useState([]);
  const [dataFetchStatus, setDataFetchStatus] = useState({});
  const screenshotRef = useRef();
  const navigate = useNavigate();

  const fetchData = async () => {
    const names = (await fetchChannelNames(vesselId, selectedChart))?.names;
    const config = (await fetchChannelConfig(vesselId))?.config;
    const data = names.map(ch => {
      const chConfig = config.find(chCon => chCon.chNo === ch.chNo);
      return {
        ...ch,
        chType: chConfig?.chType,
        sensorType: chConfig?.sensorType,
        status: chConfig?.status,
      }
    });
    const sortedChannels = stableSort(data, getComparator("desc", "chNo"));
    setChannels(sortedChannels ?? []);
  };

  useEffect(() => {
    if (selectedGraph === GRAPH_TYPE.CUSTOM) {
      dispatch(setCustomDataDuration(durationInitialState()));
      fetchData();
    }
  }, [selectedGraph, vesselId]);

  useEffect(() => {
    setSelectedChartData(customCharts?.find((e) => e.id === selectedChart));
    if (isCreateCustomCharts && selectedChart !== DEFAULT_CUSTOM_CHART_KEY) {
      navigate(`/trend/${selectedGraph}/${selectedChart}/${vesselId}`);
    }
    if (selectedChart !== DEFAULT_CUSTOM_CHART_KEY) {
      setIsCreateCustomCharts(false);
    }
  }, [selectedChart, customCharts]);
  return (
    <Grid container sx={styles.chartContainer} >
      {isCreateCustomCharts ? (
        <CustomGraph 
            vesselId={vesselId}
            currentVessel={currentVessel}
            setIsCustomChartsCreated={setIsCustomChartsCreated}
            setIsCreateCustomCharts={setIsCreateCustomCharts}
            setIsChartDeleted={setIsChartDeleted}
            customCharts={customCharts}
            customChartTitle={customChartTitle}
            setCustomChartTitle={setCustomChartTitle}
            selectedChartData={selectedChartData}
            setSelectedChartData={setSelectedChartData}
            channels={channels} />
      ) : (
        <Grid item size={{xs: 12}} ref={screenshotRef} sx={{ height: "calc(100dvh - 165px)", overflowY: "hidden" }}>
          <Grid item size={{xs: 12}} sx={{ height: "160px" }}>
            <Grid item size={{xs:12}} sx={styles.titleContainer}>
              <Box component={"div"} sx={styles.chartTitle}>{selectedChartData?.name}</Box>
              {selectedChartData?.updatedAt && (
                <Box component={"div"} sx={styles.lastUpdate}>
                  {t(localizationKeys.CustomLastEdited)}{" "}
                  {dayjs.utc(selectedChartData?.updatedAt).format(LATEST_UPDATE_FORMAT)} (UTC)
                </Box>
              )}
            </Grid>
            <DisplayGraphHeader
              vesselId={vesselId}
              isSepVessel={isSepVessel}
              selectedGraph={selectedGraph}
              selectedChart={selectedChart}
              selectedChartData={selectedChartData}
              selectedEngine={selectedEngine}
              setSelectedEngine={setSelectedEngine}
              graphData={graphData}
              dataFetchStatus={dataFetchStatus}
              currentVessel={currentVessel}
              setIsCreateCustomCharts={setIsCreateCustomCharts}
              screenshotRef={screenshotRef}
            />
          </Grid>
          <Grid item size={{xs: 12}} sx={{ height: "calc(100dvh - 300px)", overflowY: "auto" }}>
            <DisplayChart
              graphData={graphData}
              setGraphData={setGraphData}
              dataFetchStatus={dataFetchStatus}
              setDataFetchStatus={setDataFetchStatus}
              channels={channels}
              vesselId={vesselId}
              currentVessel={currentVessel}
              trendCharts={trendCharts}
              isVesselOverview={isVesselOverview}
              setChartRef={setChartRef}
              selectedGraph={selectedGraph}
              selectedChart ={selectedChart}
              customCharts={customCharts}
              isCustomChartsCreate={isCreateCustomCharts}
              selectedEngine={selectedEngine}
              setSelectedEngine={setSelectedEngine}
              overviewTrendIdx={overviewTrendIdx}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

DisplayCustomGraphPage.propTypes = {
  vesselId: PropTypes.string,
  customCharts: PropTypes.array,
  isCreateCustomCharts: PropTypes.bool,
  setIsCreateCustomCharts: PropTypes.func,
  selectedChart: PropTypes.string,
  selectedGraph: PropTypes.string,
  isSepVessel: PropTypes.bool,
  selectedEngine: PropTypes.string,
  setSelectedEngine: PropTypes.func,
  currentVessel: PropTypes.object,
  trendCharts: PropTypes.array,
  isVesselOverview: PropTypes.bool,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
  setIsCustomChartsCreated: PropTypes.func,
  setIsChartDeleted: PropTypes.func,
  customChartTitle: PropTypes.string,
  setCustomChartTitle: PropTypes.func,
}